/* eslint-disable react/jsx-props-no-spreading */
import type { Role } from 'payments-api/model';
import {
  useGetRoles,
  usePostUsers,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { CreateRoleModal } from './CreateRoleModal';

const CREATE_ROLE_SENTINEL = '__create_role__';

type FormData = {
  externalId: string;
};

type CreateUserModalProps = {
  onClose: () => void;
  onCreated: (externalId: string) => void;
};

export const CreateUserModal = ({
  onClose,
  onCreated,
}: CreateUserModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postUserMutation = usePostUsers();
  const { data: rolesData } = useGetRoles();
  const [selectedRole, setSelectedRole] = useState('');
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);

  const roles: Role[] = rolesData?.data ?? [];

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { externalId: '' },
  });

  const handleRoleChange = useCallback((value: string) => {
    if (value === CREATE_ROLE_SENTINEL) {
      setShowCreateRoleModal(true);
    } else {
      setSelectedRole(value);
    }
  }, []);

  const handleRoleCreated = useCallback((roleName: string) => {
    setSelectedRole(roleName);
    setShowCreateRoleModal(false);
  }, []);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postUserMutation.mutateAsync({
          data: {
            externalId: formData.externalId.trim(),
            role: selectedRole,
          },
        });
        await queryClient.invalidateQueries({ queryKey: ['/users'] });
        showToast(<Toast type="success" message="User created successfully" />);
        onCreated(formData.externalId.trim());
        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create user. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create user: ${errorMessage}`}
          />
        );
      }
    },
    [postUserMutation, selectedRole, queryClient, showToast, onCreated, onClose]
  );

  const roleOptions = [
    ...roles.map((r) => ({ value: r.role, label: r.role })),
    { value: CREATE_ROLE_SENTINEL, label: 'New+' },
  ];

  const canSubmit =
    isValid && selectedRole.trim() !== '' && !postUserMutation.isPending;

  return (
    <>
      <Modal
        data-testid="create-user-modal"
        onClose={onClose}
        width="sm"
        onSubmit={handleSubmit(onSubmit)}
        header={<div className="text-main">Create User</div>}
        body={
          <div className="flex flex-col space-y-f2">
            <ModalField
              hasError={!!errors.externalId}
              label="External ID"
              labelWidth="w-f12"
            >
              <ModalTextInput
                data-testid="user-external-id-input"
                placeholder="External ID"
                {...register('externalId', {
                  required: 'External ID is required',
                  validate: (value) =>
                    value.trim() !== '' || 'External ID cannot be empty',
                })}
              />
            </ModalField>
            <ModalField label="Role" labelWidth="w-f12" overflow>
              <Dropdown
                options={roleOptions}
                onChange={handleRoleChange}
                displayValue={selectedRole}
                placeholder="Select a role"
                size="md"
                width="w-full"
                height="h-fit"
                align="left"
                data-testid="user-role-dropdown"
              />
            </ModalField>
          </div>
        }
        footer={
          <div className="flex flex-row justify-between w-full">
            <FormButtons
              type="secondary"
              cancelButtonLabel="Cancel"
              submitButtonLabel={
                postUserMutation.isPending ? 'Creating...' : 'Create'
              }
              onCancel={onClose}
              onSubmit={handleSubmit(onSubmit)}
              disabled={!canSubmit}
            />
          </div>
        }
      />
      {showCreateRoleModal && (
        <CreateRoleModal
          onClose={() => setShowCreateRoleModal(false)}
          onCreated={handleRoleCreated}
        />
      )}
    </>
  );
};
